import type {
  FetchNextPageOptions,
  FetchPreviousPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters
} from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import type { AnyVariables, Client, DocumentInput } from 'urql'
import { useGraphQLRequest } from '~/core/hooks/use-graphql'
import useContextGraphQL from '~/core/middleware/use-context-graphql'
import { catchErrorFromGraphQL } from '~/core/utilities/catch-api-error'

export type InfinityGraphPageReturnDataType<Data = {}> = {
  data: InfiniteData<Data> | undefined
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<Data, {}>>
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<InfiniteData<Data>, {}>>
  hasNextPage: boolean | undefined
  isFetching: boolean
  fetchPreviousPage: (
    options?: FetchPreviousPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<Data, {}>>
  isFetchedAfterMount: boolean
  isLoading: boolean
}

export const useInfinityGraphPageWithClient = <
  Data = any,
  Variables extends AnyVariables = AnyVariables
>({
  queryDocumentNote,
  getVariable,
  getPageAttribute,
  queryKey,
  enabled = true,
  client
}: {
  queryDocumentNote: DocumentInput<Data, Variables>
  getVariable: (page: number) => Partial<Variables>
  getPageAttribute: (last: Data, group: Data[]) => { totalCount: number; pageLength: number }
  queryKey: string[]
  enabled?: boolean
  client: Client
}) => {
  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchedAfterMount,
    isLoading
  } = useInfiniteQuery<Data, {}, Data, string[]>(
    queryKey,
    useCallback(
      (context) => {
        return client
          .query(queryDocumentNote, getVariable(context.pageParam || 1))
          .toPromise()
          .then((rs) => {
            if (rs.error) {
              catchErrorFromGraphQL({
                error: rs.error
              })
              return {} as Data
            }
            return rs.data || ({} as Data)
          })
      },
      [getVariable, queryDocumentNote, client, catchErrorFromGraphQL]
    ),
    {
      getNextPageParam: (_lastGroup, groups) => {
        const { totalCount, pageLength } = getPageAttribute(_lastGroup, groups)

        const isHaveNext = totalCount > 0 && totalCount > groups.length * pageLength
        return isHaveNext ? groups.length + 1 : undefined
      },
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  )
  return useMemo(
    () => ({
      data,
      fetchNextPage,
      fetchPreviousPage,
      refetch,
      hasNextPage,
      isFetching,
      isFetchedAfterMount,
      isLoading
    }),
    [
      data,
      fetchNextPage,
      refetch,
      hasNextPage,
      isFetching,
      fetchPreviousPage,
      isFetchedAfterMount,
      isLoading
    ]
  )
}

// Original hook with new parameter to choose GraphQL endpoint
export const useInfinityGraphPage = <Data = any, Variables extends AnyVariables = AnyVariables>({
  queryDocumentNote,
  getVariable,
  getPageAttribute,
  queryKey,
  enabled = true,
  useTfRagGraphQL = false
}: {
  queryDocumentNote: DocumentInput<Data, Variables>
  getVariable: (page: number) => Partial<Variables>
  getPageAttribute: (last: Data, group: Data[]) => { totalCount: number; pageLength: number }
  queryKey: string[]
  enabled?: boolean
  useTfRagGraphQL?: boolean
}) => {
  const { clientGraphQL } = useContextGraphQL()

  // Create custom client if needed using tfRagGraphQL
  const tfRagClient = useGraphQLRequest({
    language: undefined,
    isCareerHub: true,
    isTfRagGraphQL: true
  })

  const client = useTfRagGraphQL ? tfRagClient : clientGraphQL

  return useInfinityGraphPageWithClient({
    queryDocumentNote,
    getVariable,
    getPageAttribute,
    queryKey,
    enabled,
    client
  })
}
